import { FormEvent, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Briefcase,
  Calendar,
  CalendarCheck,
  ChevronDown,
  ChevronUp,
  Crown,
  Download,
  FileSpreadsheet,
  FileText,
  GraduationCap,
  Pencil,
  Plus,
  Printer,
  Search,
  TrendingUp,
  UserMinus,
  Users,
  WalletCards,
  X,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { LeaveRequest, SalaryChangeRequest, StaffProfile, staffService } from "../services/staffService";
import { usePermissions } from "../hooks/usePermissions";
import { Modal } from "../components/Modal";
import { exportAsCsv, exportAsExcel, exportAsPdf } from "../utils/exportTable";
import StaffProfileModal from "../components/staff/StaffProfileModal";
import PayChangeReviewModal, { PayChangeStatus } from "../components/staff/PayChangeReviewModal";
import LeaveRequestModal from "../components/staff/LeaveRequestModal";
import RecordAttendanceModal from "../components/staff/RecordAttendanceModal";
import ScheduleTrainingModal from "../components/staff/ScheduleTrainingModal";
import TrainingSessionModal, { TrainingPhaseLabel } from "../components/staff/TrainingSessionModal";
import { Pager, paginate } from "../components/Pager";
import {
  activeTrainingFilterCount,
  COMPLETION_RESULT_TABS,
  completionCounts as countCompletions,
  EMPTY_TRAINING_FILTERS,
  filterCompletions,
  filterTraining,
  sessionStats,
  TRAINING_PHASE_TABS,
  TRAINING_TYPES,
  trainingPhaseCounts,
  trainingTypeLabel,
  type TrainingFilters,
} from "./trainingFilters";
import {
  activeAttendanceFilterCount,
  ATTENDANCE_STATUS_DOTS,
  ATTENDANCE_STATUS_TABS,
  attendanceStatusCounts,
  EMPTY_ATTENDANCE_FILTERS,
  filterAttendance,
  shortTime,
  weekStart,
  type AttendanceFilters,
} from "./attendanceFilters";
import LeaveReviewModal, { LeaveStatus } from "../components/staff/LeaveReviewModal";
import {
  activeLeaveFilterCount,
  EMPTY_LEAVE_FILTERS,
  filterLeaves,
  LEAVE_STATUS_TABS,
  leaveStatusCounts,
  localToday,
  type LeaveFilters,
} from "./leaveFilters";
import {
  activePayChangeFilterCount,
  EMPTY_PAY_CHANGE_FILTERS,
  filterPayChanges,
  grossChange,
  PAY_CHANGE_STATUS_TABS,
  payChangeStatusCounts,
  type PayChangeFilters,
} from "./payChangeFilters";
import EditEmploymentModal from "../components/staff/EditEmploymentModal";
import EndEmploymentModal, { type EndEmploymentValues } from "../components/staff/EndEmploymentModal";
import PayChangeModal, {
  describeChange,
  totalOf,
  type PayChangeTarget,
  type PayChangeValues,
} from "../components/staff/PayChangeModal";
import { StaffStatus } from "../components/staff/StaffStatus";
import { formatSchoolDate } from "../components/students/studentDisplay";
import {
  activeStaffFilterCount,
  EMPTY_STAFF_FILTERS,
  filterStaffProfiles,
  STAFF_DIRECTORY_PAGE_SIZES,
  STAFF_STATUS_TABS,
  type StaffDirectoryFilters,
  type StaffSortKey,
} from "./staffDirectoryFilters";

export default function Staff() {
  const { hasCapability, user } = usePermissions(),
    canManage = hasCapability("access.staff.manager"),
    canApproveSalary = hasCapability("access.staff.approve_salary_changes"),
    qc = useQueryClient(),
    [searchParams, setSearchParams] = useSearchParams(),
    [tab, setTab] = useState("directory"),
    [offboarding, setOffboarding] = useState<number | null>(null),
    [payChange, setPayChange] = useState<PayChangeTarget | null>(null),
    [editingProfile, setEditingProfile] = useState<StaffProfile | null>(null),
    [viewingProfile, setViewingProfile] = useState<StaffProfile | null>(null),
    [addProfileOpen, setAddProfileOpen] = useState(false),
    [staffFilters, setStaffFilters] = useState(EMPTY_STAFF_FILTERS),
    [profilePageSize, setProfilePageSize] = useState(25),
    [sortKey, setSortKey] = useState<StaffSortKey>("user_name"),
    [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc"),
    [profilePage, setProfilePage] = useState(1),
    [isExporting, setIsExporting] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const departments = useQuery({
      queryKey: ["staff-departments"],
      queryFn: staffService.getDepartments,
    }),
    positions = useQuery({
      queryKey: ["staff-positions"],
      queryFn: staffService.getPositions,
    }),
    profiles = useQuery({
      queryKey: ["staff-profiles"],
      queryFn: staffService.getProfiles,
    }),
    leaveTypes = useQuery({
      queryKey: ["staff-leave-types"],
      queryFn: staffService.getLeaveTypes,
    }),
    leaves = useQuery({
      queryKey: ["staff-leave"],
      queryFn: staffService.getLeave,
    }),
    attendance = useQuery({
      queryKey: ["staff-attendance"],
      queryFn: staffService.getAttendance,
    }),
    training = useQuery({
      queryKey: ["staff-training"],
      queryFn: staffService.getTraining,
    }),
    trainingAttendance = useQuery({
      queryKey: ["staff-training-attendance"],
      queryFn: staffService.getTrainingAttendance,
    }),
    salaryChanges = useQuery({
      queryKey: ["staff-salary-changes"],
      queryFn: staffService.getSalaryChangeRequests,
      enabled: canManage || canApproveSalary,
    }),
    eligible = useQuery({
      queryKey: ["staff-eligible"],
      queryFn: staffService.getEligibleUsers,
      enabled: canManage,
    }),
    stats = useQuery({
      queryKey: ["staff-stats"],
      queryFn: staffService.getStats,
    });
  const requestedUserId = Number(searchParams.get("user")) || null;
  useEffect(() => {
    if (!canManage || !requestedUserId || !profiles.data) return;
    const requestedProfile = profiles.data.find(
      (profile) => profile.user === requestedUserId,
    );
    if (!requestedProfile) return;
    setTab("directory");
    setStaffFilters({ ...EMPTY_STAFF_FILTERS, search: requestedProfile.user_username });
    setEditingProfile(requestedProfile);
    setSearchParams({}, { replace: true });
  }, [
    canManage,
    profiles.data,
    requestedUserId,
    setSearchParams,
  ]);
  const visibleProfiles = useMemo(
    () => filterStaffProfiles(profiles.data ?? [], staffFilters, sortKey, sortDirection),
    [profiles.data, sortDirection, sortKey, staffFilters],
  );
  const activeStaffFilters = activeStaffFilterCount(staffFilters);
  const offboardingProfile = profiles.data?.find((profile) => profile.id === offboarding) ?? null;
  const [payFilters, setPayFilters] = useState(EMPTY_PAY_CHANGE_FILTERS),
    [reviewingPayChangeId, setReviewingPayChangeId] = useState<number | null>(null);
  const updatePayFilters = (changes: Partial<PayChangeFilters>) =>
    setPayFilters((current) => ({ ...current, ...changes }));
  // Counts on the status tabs follow the other filters, so they add up.
  const payChangesBeforeStatus = useMemo(
    () => filterPayChanges(salaryChanges.data ?? [], payFilters, profiles.data ?? [], { ignoreStatus: true }),
    [payFilters, profiles.data, salaryChanges.data],
  );
  const payStatusCounts = payChangeStatusCounts(payChangesBeforeStatus);
  const visiblePayChanges = payFilters.status
    ? payChangesBeforeStatus.filter((change) => change.workflow_status === payFilters.status)
    : payChangesBeforeStatus;
  const activePayFilters = activePayChangeFilterCount(payFilters);
  const reviewingPayChange =
    salaryChanges.data?.find((change) => change.id === reviewingPayChangeId) ?? null;
  const [leaveFilters, setLeaveFilters] = useState(EMPTY_LEAVE_FILTERS),
    [requestingLeave, setRequestingLeave] = useState(false),
    [reviewingLeaveId, setReviewingLeaveId] = useState<number | null>(null),
    [leavePage, setLeavePage] = useState(1),
    [leavePageSize, setLeavePageSize] = useState(25);
  const updateLeaveFilters = (changes: Partial<LeaveFilters>) => {
    setLeaveFilters((current) => ({ ...current, ...changes }));
    setLeavePage(1);
  };
  const leavesBeforeStatus = useMemo(
    () => filterLeaves(leaves.data ?? [], leaveFilters, profiles.data ?? [], { ignoreStatus: true }),
    [leaveFilters, leaves.data, profiles.data],
  );
  const leaveCounts = leaveStatusCounts(leavesBeforeStatus);
  const visibleLeaves = useMemo(
    () => filterLeaves(leaves.data ?? [], leaveFilters, profiles.data ?? []),
    [leaveFilters, leaves.data, profiles.data],
  );
  const activeLeaveFilters = activeLeaveFilterCount(leaveFilters);
  const leavePageCount = Math.max(1, Math.ceil(visibleLeaves.length / leavePageSize));
  const pagedLeaves = visibleLeaves.slice((leavePage - 1) * leavePageSize, leavePage * leavePageSize);
  const firstLeaveRow = visibleLeaves.length ? (leavePage - 1) * leavePageSize + 1 : 0;
  const reviewingLeave = leaves.data?.find((leave) => leave.id === reviewingLeaveId) ?? null;
  // The signed-in person's own staff record, for "Myself" and cancelling.
  const ownProfileId = profiles.data?.find((profile) => profile.user === user?.id)?.id;
  const [attendanceFilters, setAttendanceFilters] = useState(EMPTY_ATTENDANCE_FILTERS),
    [attendancePage, setAttendancePage] = useState(1),
    [recordingAttendance, setRecordingAttendance] = useState(false);
  const attendancePageSize = 25;
  const updateAttendanceFilters = (changes: Partial<AttendanceFilters>) => {
    setAttendanceFilters((current) => ({ ...current, ...changes }));
    setAttendancePage(1);
  };
  // Counts on the status tabs follow the other filters, so they add up.
  const attendanceBeforeStatus = useMemo(
    () => filterAttendance(attendance.data ?? [], attendanceFilters, profiles.data ?? [], { ignoreStatus: true }),
    [attendance.data, attendanceFilters, profiles.data],
  );
  const attendanceCounts = attendanceStatusCounts(attendanceBeforeStatus);
  const visibleAttendance = useMemo(
    () => filterAttendance(attendance.data ?? [], attendanceFilters, profiles.data ?? []),
    [attendance.data, attendanceFilters, profiles.data],
  );
  const activeAttendanceFilters = activeAttendanceFilterCount(attendanceFilters);
  const attendancePageCount = Math.max(1, Math.ceil(visibleAttendance.length / attendancePageSize));
  const pagedAttendance = visibleAttendance.slice(
    (attendancePage - 1) * attendancePageSize,
    attendancePage * attendancePageSize,
  );
  const firstAttendanceRow = visibleAttendance.length ? (attendancePage - 1) * attendancePageSize + 1 : 0;
  const profileById = useMemo(
    () => new Map((profiles.data ?? []).map((profile) => [profile.id, profile])),
    [profiles.data],
  );
  const [trainingView, setTrainingView] = useState<"sessions" | "register">("sessions"),
    [trainingFilters, setTrainingFilters] = useState(EMPTY_TRAINING_FILTERS),
    [trainingPageRequested, setTrainingPage] = useState(1),
    [completionSearch, setCompletionSearch] = useState(""),
    [completionResult, setCompletionResult] = useState(""),
    [completionPageRequested, setCompletionPage] = useState(1),
    [schedulingTraining, setSchedulingTraining] = useState(false),
    [viewingTrainingId, setViewingTrainingId] = useState<number | null>(null);
  const updateTrainingFilters = (changes: Partial<TrainingFilters>) => {
    setTrainingFilters((current) => ({ ...current, ...changes }));
    setTrainingPage(1);
  };
  const trainingBeforePhase = useMemo(
    () => filterTraining(training.data ?? [], trainingFilters, { ignorePhase: true }),
    [training.data, trainingFilters],
  );
  const trainingCounts = trainingPhaseCounts(trainingBeforePhase);
  const visibleTraining = useMemo(
    () => filterTraining(training.data ?? [], trainingFilters),
    [training.data, trainingFilters],
  );
  const activeTrainingFilters = activeTrainingFilterCount(trainingFilters);
  const {
    rows: pagedTraining,
    first: firstTrainingRow,
    page: trainingPage,
    pageCount: trainingPageCount,
  } = paginate(visibleTraining, trainingPageRequested, 25);
  const completionsBeforeResult = useMemo(
    () => filterCompletions(trainingAttendance.data ?? [], completionSearch),
    [completionSearch, trainingAttendance.data],
  );
  const completionCounts = countCompletions(completionsBeforeResult);
  const visibleCompletions = useMemo(
    () => filterCompletions(trainingAttendance.data ?? [], completionSearch, completionResult),
    [completionResult, completionSearch, trainingAttendance.data],
  );
  const {
    rows: pagedCompletions,
    first: firstCompletionRow,
    page: completionPage,
    pageCount: completionPageCount,
  } = paginate(visibleCompletions, completionPageRequested, 25);
  const viewingTraining = training.data?.find((session) => session.id === viewingTrainingId) ?? null;
  const openLeaveReview = (leave: LeaveRequest) => {
    approve.reset();
    reject.reset();
    cancel.reset();
    setReviewingLeaveId(leave.id);
  };
  const openPayReview = (change: SalaryChangeRequest) => {
    approveSalaryChange.reset();
    returnSalaryChange.reset();
    rejectSalaryChange.reset();
    setReviewingPayChangeId(change.id);
  };
  const updateStaffFilters = (changes: Partial<StaffDirectoryFilters>) =>
    setStaffFilters((current) => ({ ...current, ...changes }));
  const profilePageCount = Math.max(1, Math.ceil(visibleProfiles.length / profilePageSize));
  const pagedProfiles = visibleProfiles.slice((profilePage - 1) * profilePageSize, profilePage * profilePageSize);
  const firstProfileRow = visibleProfiles.length ? (profilePage - 1) * profilePageSize + 1 : 0;
  useEffect(() => setProfilePage(1), [profilePageSize, sortDirection, sortKey, staffFilters]);
  const exportDirectory = (format: "csv" | "excel" | "pdf") => {
    setIsExporting(true);
    try {
      const columns = [
        { header: "Employee", accessor: (profile: StaffProfile) => profile.user_name },
        { header: "Username", accessor: (profile: StaffProfile) => profile.user_username },
        { header: "Employee ID", accessor: (profile: StaffProfile) => profile.employee_id },
        { header: "Position", accessor: (profile: StaffProfile) => profile.position_title },
        { header: "Department", accessor: (profile: StaffProfile) => profile.department_name || "School-wide" },
        { header: "Heads", accessor: (profile: StaffProfile) => (profile.headed_departments ?? []).map((department) => department.name).join(", ") },
        { header: "Hire date", accessor: (profile: StaffProfile) => profile.hire_date },
        { header: "Work location", accessor: (profile: StaffProfile) => profile.work_location || "" },
        { header: "Status", accessor: (profile: StaffProfile) => profile.employment_status.replace(/_/g, " ") },
        { header: "Salary", accessor: (profile: StaffProfile) => profile.salary ? `${profile.currency} ${Number(profile.salary).toLocaleString()}` : "Not set" },
      ];
      const filename = `staff-directory-${new Date().toISOString().slice(0, 10)}`;
      if (format === "csv") exportAsCsv(`${filename}.csv`, columns, visibleProfiles);
      else if (format === "excel") exportAsExcel(`${filename}.xls`, "Staff directory", columns, visibleProfiles);
      else exportAsPdf("Staff directory", columns, visibleProfiles);
    } finally {
      setIsExporting(false);
    }
  };
  const refresh = () =>
    Promise.all(
      [
        "staff-departments",
        "staff-positions",
        "staff-profiles",
        "staff-leave-types",
        "staff-leave",
        "staff-attendance",
        "staff-training",
        "staff-training-attendance",
        "staff-salary-changes",
        "staff-eligible",
        "staff-stats",
      ].map((k) => qc.invalidateQueries({ queryKey: [k] })),
    );
  const useSave = <T,>(fn: (d: T) => Promise<unknown>, onSaved?: () => void) =>
    useMutation({
      mutationFn: fn,
      onSuccess: () => {
        setError("");
        setNotice("Staff record saved successfully.");
        refresh();
        onSaved?.();
      },
      onError: () =>
        setError(
          "The staff operation could not be completed. Check permissions, dates, personnel setup, and required fields.",
        ),
    });
  const addDepartment = useSave(staffService.createDepartment),
    addPosition = useSave(staffService.createPosition),
    addProfile = useSave(staffService.createProfile, () => setAddProfileOpen(false)),
    updateProfile = useSave(
      ({ id, data }: { id: number; data: Partial<StaffProfile> }) =>
        staffService.updateProfile(id, data),
    ),
    addLeaveType = useSave(staffService.createLeaveType),
    requestLeave = useSave(staffService.requestLeave),
    approve = useSave(staffService.approveLeave),
    reject = useSave(staffService.rejectLeave),
    cancel = useSave(staffService.cancelLeave),
    addAttendance = useSave(staffService.createAttendance),
    addTraining = useSave(staffService.createTraining),
    addTrainingAttendance = useSave(staffService.createTrainingAttendance),
    requestSalaryChange = useSave(staffService.createSalaryChangeRequest),
    approveSalaryChange = useSave(staffService.approveSalaryChange),
    rejectSalaryChange = useSave(staffService.rejectSalaryChange),
    returnSalaryChange = useSave(staffService.returnSalaryChange),
    reviseSalaryChange = useSave(staffService.reviseSalaryChange),
    offboard = useSave(staffService.offboard);
  const form = (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError("");
      return new FormData(e.currentTarget);
    },
    done = (e: FormEvent<HTMLFormElement>) => e.currentTarget.reset();
  const submitDepartment = (e: FormEvent<HTMLFormElement>) => {
    const d = form(e);
    addDepartment.mutate({
      name: String(d.get("name")),
      code: String(d.get("code")),
      department_type: String(d.get("department_type")) as
        | "academic"
        | "administrative"
        | "finance"
        | "student_services"
        | "operations"
        | "other",
      description: String(d.get("description")),
      is_active: true,
    });
    done(e);
  };
  const submitPosition = (e: FormEvent<HTMLFormElement>) => {
    const d = form(e);
    addPosition.mutate({
      title: String(d.get("title")),
      department: Number(d.get("department")) || null,
      level: String(d.get("level")),
      experience_required: Number(d.get("experience")),
      is_active: true,
      is_vacant: true,
    });
    done(e);
  };
  const submitProfile = (e: FormEvent<HTMLFormElement>) => {
    const d = form(e);
    const salary = String(d.get("salary")).trim();
    addProfile.mutate({
      user: Number(d.get("user")),
      position: Number(d.get("position")),
      hire_date: String(d.get("hire")),
      employment_status: "active",
      work_location: String(d.get("location")),
      salary: salary || null,
      currency: "UGX",
    });
    done(e);
  };
  const submitProfileEdit = (data: Partial<StaffProfile>) => {
    if (!editingProfile) return;
    setError("");
    updateProfile.mutate(
      { id: editingProfile.id, data },
      { onSuccess: () => setEditingProfile(null) },
    );
  };
  const openOffboarding = (id: number) => {
    offboard.reset();
    setOffboarding(id);
  };
  const openEditProfile = (profile: StaffProfile) => {
    updateProfile.reset();
    setEditingProfile(profile);
  };
  // Opening the form against a staff record raises a new pay change; opening it
  // against a returned request revises that one instead.
  const payRangeFor = (positionId?: number) => {
      const position = positions.data?.find((item) => item.id === positionId);
      const min = Number(position?.salary_range_min ?? 0);
      const max = Number(position?.salary_range_max ?? 0);
      return position && (min > 0 || max > 0)
        ? { title: position.title, range: { min, max } }
        : { title: position?.title };
    },
    openPayChange = (profile: StaffProfile) => {
      const { title, range } = payRangeFor(profile.position);
      requestSalaryChange.reset();
      reviseSalaryChange.reset();
      setPayChange({
        staffId: profile.id,
        name: profile.user_name,
        currency: profile.currency,
        currentSalary: profile.salary ?? null,
        currentAllowanceTotal: Number(profile.allowances ?? 0),
        startingAllowances: profile.allowance_lines ?? [],
        positionTitle: title,
        payRange: range,
      });
    },
    openRevision = (change: SalaryChangeRequest) => {
      const profile = profiles.data?.find((item) => item.id === change.staff);
      const { title, range } = payRangeFor(profile?.position);
      requestSalaryChange.reset();
      reviseSalaryChange.reset();
      setPayChange({
        staffId: change.staff,
        name: change.staff_name,
        currency: change.currency,
        currentSalary: change.previous_salary,
        currentAllowanceTotal: totalOf(change.previous_allowances ?? []),
        startingAllowances: change.proposed_allowances ?? [],
        positionTitle: title,
        payRange: range,
        requestId: change.id,
        proposedSalary: change.proposed_salary,
        effectiveDate: change.effective_date,
        reason: change.reason,
        returnReason: change.return_reason,
      });
    },
    submitPayChange = (values: PayChangeValues) => {
      setError("");
      const done = { onSuccess: () => setPayChange(null) };
      if (payChange?.requestId) {
        reviseSalaryChange.mutate({ id: payChange.requestId, ...values }, done);
      } else if (payChange) {
        requestSalaryChange.mutate({ staff: payChange.staffId, ...values }, done);
      }
    };
  const submitOffboarding = (values: EndEmploymentValues) => {
    if (!offboarding) return;
    setError("");
    offboard.mutate(
      { id: offboarding, ...values },
      {
        onSuccess: (result) => {
          setOffboarding(null);
          const ended =
            (result as { ended_teaching_assignments?: Array<{ class_name: string; subject_name: string }> })
              ?.ended_teaching_assignments ?? [];
          if (ended.length) {
            setNotice(
              `Employment ended. These classes now need a teacher: ${ended
                .map((item) => `${item.subject_name} (${item.class_name})`)
                .join(", ")}.`,
            );
          }
        },
      },
    );
  };
  const submitLeaveType = (e: FormEvent<HTMLFormElement>) => {
    const d = form(e);
    addLeaveType.mutate({
      name: String(d.get("name")),
      code: String(d.get("code")),
      max_days_per_year: Number(d.get("days")),
      is_paid: true,
      requires_approval: true,
      is_active: true,
    });
    done(e);
  };
  const changeSort = (key: StaffSortKey) => {
    if (sortKey === key) setSortDirection((direction) => direction === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };
  const sortButton = (label: string, key: StaffSortKey) => (
    <button type="button" onClick={() => changeSort(key)} className="inline-flex items-center gap-1 font-semibold hover:text-gray-900">
      {label}
      {sortKey === key && (sortDirection === "asc" ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />)}
    </button>
  );
  // "Department" (below) is the one this person belongs to; this is the
  // department(s) they're actually head of -- a different relationship the
  // directory previously had no way to show at all, short of opening the
  // admin Setup tab and scanning every department's own head field by hand.
  const headedDepartmentsBadges = (departments?: Array<{ id: number; name: string }>) => {
    if (!departments || departments.length === 0) return null;
    return (
      <span className="ml-1.5 inline-flex flex-wrap gap-1 align-middle">
        {departments.map((dept) => (
          <span key={dept.id} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-amber-200" title={`Heads ${dept.name}`}>
            <Crown className="h-2.5 w-2.5" /> {dept.name}
          </span>
        ))}
      </span>
    );
  };
  const statusDot = (status: string) => <StaffStatus status={status} />;
  const exportRecords = <T,>(
    format: "csv" | "excel" | "pdf",
    title: string,
    sheetName: string,
    columns: Array<{ header: string; accessor: (row: T) => string | number }>,
    rows: T[],
  ) => {
    setIsExporting(true);
    try {
      const filename = `${sheetName.toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}`;
      if (format === "csv") exportAsCsv(`${filename}.csv`, columns, rows);
      else if (format === "excel") exportAsExcel(`${filename}.xls`, sheetName, columns, rows);
      else exportAsPdf(title, columns, rows);
    } finally {
      setIsExporting(false);
    }
  };
  const exportSalaryChanges = (format: "csv" | "excel" | "pdf") =>
    exportRecords(format, "Pay change approvals", "Pay changes", [
      { header: "Staff member", accessor: (change) => change.staff_name },
      { header: "Previous basic pay", accessor: (change) => `${change.currency} ${Number(change.previous_salary).toLocaleString()}` },
      { header: "Proposed basic pay", accessor: (change) => `${change.currency} ${Number(change.proposed_salary).toLocaleString()}` },
      { header: "Previous allowances", accessor: (change) => totalOf(change.previous_allowances ?? []).toLocaleString() },
      { header: "Proposed allowances", accessor: (change) => (change.proposed_allowances ?? []).map((line) => `${line.name} ${Number(line.amount).toLocaleString()}`).join("; ") || "None" },
      { header: "Gross change", accessor: (change) => { const gross = grossChange(change); return describeChange(gross.before, gross.after); } },
      { header: "Effective date", accessor: (change) => change.effective_date },
      { header: "Status", accessor: (change) => change.workflow_status.replace(/_/g, " ") },
      { header: "Requested by", accessor: (change) => change.initiated_by_name },
      { header: "Reason", accessor: (change) => change.reason },
      { header: "Decided by", accessor: (change) => change.approved_by_name || "" },
    ], visiblePayChanges);
  const exportLeave = (format: "csv" | "excel" | "pdf") =>
    exportRecords(format, "Staff leave register", "Leave register", [
      { header: "Staff member", accessor: (leave) => leave.staff_name },
      { header: "Leave type", accessor: (leave) => leave.leave_type_name },
      { header: "Start date", accessor: (leave) => leave.start_date },
      { header: "End date", accessor: (leave) => leave.end_date },
      { header: "Days", accessor: (leave) => leave.total_days },
      { header: "Status", accessor: (leave) => leave.status },
      { header: "Reason", accessor: (leave) => leave.reason },
      { header: "Decided by", accessor: (leave) => leave.approved_by_name || "" },
      { header: "Decision notes", accessor: (leave) => leave.approval_notes || "" },
    ], visibleLeaves);
  const exportAttendance = (format: "csv" | "excel" | "pdf") =>
    exportRecords(format, "Staff attendance register", "Attendance", [
      { header: "Staff member", accessor: (record) => record.staff_name },
      { header: "Date", accessor: (record) => record.date },
      { header: "Status", accessor: (record) => record.status },
      { header: "Check in", accessor: (record) => record.check_in_time || "Not recorded" },
      { header: "Check out", accessor: (record) => record.check_out_time || "Not recorded" },
      { header: "Hours worked", accessor: (record) => record.hours_worked },
      { header: "Notes", accessor: (record) => record.notes || "" },
    ], visibleAttendance);
  const exportTraining = (format: "csv" | "excel" | "pdf") =>
    exportRecords(format, "Staff training register", "Training", [
      { header: "Training", accessor: (item) => item.name },
      { header: "Type", accessor: (item) => trainingTypeLabel(item.training_type) },
      { header: "Start date", accessor: (item) => item.start_date },
      { header: "End date", accessor: (item) => item.end_date },
      { header: "Duration (hours)", accessor: (item) => item.duration_hours },
      { header: "Venue", accessor: (item) => item.venue || "Not set" },
      { header: "Trainer", accessor: (item) => item.trainer || "Not set" },
      { header: "Mandatory", accessor: (item) => item.is_mandatory ? "Yes" : "No" },
      { header: "Attended", accessor: (item) => sessionStats(item.id, trainingAttendance.data ?? []).attended },
      { header: "Passed", accessor: (item) => sessionStats(item.id, trainingAttendance.data ?? []).passed },
    ], visibleTraining);
  const exportTrainingAttendance = (format: "csv" | "excel" | "pdf") =>
    exportRecords(format, "Training completion register", "Training completions", [
      { header: "Training", accessor: (item) => item.training_name },
      { header: "Staff member", accessor: (item) => item.staff_name },
      { header: "Attended", accessor: (item) => item.attended ? "Yes" : "No" },
      { header: "Score", accessor: (item) => item.score || "Not recorded" },
      { header: "Passed", accessor: (item) => item.passed ? "Yes" : "No" },
      { header: "Completed", accessor: (item) => item.completion_date || "" },
      { header: "Feedback", accessor: (item) => item.feedback || "" },
    ], visibleCompletions);
  const tabs = [
      ["directory", "Directory", Users],
      ["salary", "Pay changes", WalletCards],
      ["leave", "Leave", Calendar],
      ["attendance", "Attendance", TrendingUp],
      ["training", "Training", GraduationCap],
    ] as const,
    cards = [
      ["Active staff", stats.data?.active_staff ?? 0, Users],
      ["Departments", stats.data?.total_departments ?? 0, Briefcase],
      ["On leave", stats.data?.staff_on_leave ?? 0, Calendar],
      ["Pending leave", stats.data?.pending_leave ?? 0, TrendingUp],
    ] as const;
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Staff Management</h1>
        <p className="text-gray-600">
          Personnel records, leave, attendance, performance, and development
        </p>
      </div>
      {error && (
        <div className="rounded bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map(([l, v, I]) => (
          <div key={l} className="card">
            <div className="card-content flex items-center">
              <I className="h-8 w-8 text-primary-600" />
              <div className="ml-5">
                <div className="text-sm text-gray-500">{l}</div>
                <div className="text-2xl font-semibold">{v}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <nav className="flex flex-wrap gap-x-6 gap-y-2 border-b">
        {tabs
          .filter(([id]) => id !== "salary" || canManage || canApproveSalary)
          .map(([id, l, I]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`whitespace-nowrap border-b-2 py-2 text-sm ${tab === id ? "border-primary-500 text-primary-600" : "border-transparent text-gray-500"}`}
            >
              <I className="mr-2 inline h-4 w-4" />
              {l}
            </button>
          ))}
      </nav>
      {tab === "directory" && (
        <div className="space-y-4">
          {notice && <div role="status" className="rounded bg-green-50 p-3 text-sm text-green-700">{notice}</div>}
          {profiles.isError ? <div className="card"><div className="card-content"><div className="rounded bg-red-50 p-4 text-sm text-red-700">Staff profiles could not be loaded. Please try again.</div><button type="button" className="btn btn-outline mt-3" onClick={() => profiles.refetch()}>Retry</button></div></div> : null}
          {canManage && (
            <Modal isOpen={addProfileOpen} size="2xl" title="Add staff member" onClose={() => setAddProfileOpen(false)}>
              <form onSubmit={submitProfile} className="grid gap-4 md:grid-cols-2">
                <label className="text-sm font-medium">Eligible user *<select required name="user" className="input mt-1"><option value="">Select user</option>{eligible.data?.map((u) => <option key={u.id} value={u.id}>{u.full_name} · {u.user_type}</option>)}</select></label>
                <label className="text-sm font-medium">Position *<select required name="position" className="input mt-1"><option value="">Select position</option>{positions.data?.map((p) => <option key={p.id} value={p.id}>{p.title} · {p.department_name || "School-wide"}</option>)}</select></label>
                <label className="text-sm font-medium">Hire date *<input required type="date" name="hire" className="input mt-1" /></label>
                <label className="text-sm font-medium">Work location<input name="location" className="input mt-1" placeholder="Main campus" /></label>
                <label className="text-sm font-medium">Initial salary<input min="0" type="number" name="salary" className="input mt-1" placeholder="Optional" /></label>
                <div className="flex items-end justify-end gap-2 md:col-span-2"><button type="button" className="btn btn-outline" onClick={() => setAddProfileOpen(false)}>Cancel</button><button disabled={addProfile.isPending} className="btn btn-primary">{addProfile.isPending ? "Creating…" : "Create staff member"}</button></div>
              </form>
            </Modal>
          )}
          {canManage && editingProfile && (
            <EditEmploymentModal
              profile={editingProfile}
              positions={positions.data ?? []}
              isSaving={updateProfile.isPending}
              error={updateProfile.error}
              onSubmit={submitProfileEdit}
              onPayChange={() => {
                const profile = editingProfile;
                setEditingProfile(null);
                openPayChange(profile);
              }}
              onClose={() => {
                setError("");
                setEditingProfile(null);
              }}
            />
          )}
          {canManage && offboardingProfile && (
            <EndEmploymentModal
              profile={offboardingProfile}
              isSaving={offboard.isPending}
              error={offboard.error}
              onSubmit={submitOffboarding}
              onClose={() => setOffboarding(null)}
            />
          )}
          <div className="card">
            <div className="card-header pb-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <Users className="h-5 w-5" /> Staff directory
                  </h2>
                  <p className="text-sm text-gray-500">
                    Employment, position and pay for everyone on the staff list.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">Export</span>
                  {(
                    [
                      ["csv", FileText, "CSV", "Download the filtered staff directory as CSV"],
                      ["excel", FileSpreadsheet, "Excel", "Download the filtered staff directory as Excel"],
                      ["pdf", Printer, "PDF", "Print or save the filtered staff directory as PDF"],
                    ] as const
                  ).map(([format, Icon, label, title]) => (
                    <button
                      key={format}
                      type="button"
                      disabled={isExporting || visibleProfiles.length === 0}
                      onClick={() => exportDirectory(format)}
                      className="pager-button h-8"
                      title={title}
                    >
                      <Icon className="h-3.5 w-3.5" /> {label}
                    </button>
                  ))}
                  {canManage && (
                    <button
                      type="button"
                      className="btn btn-primary ml-1 h-8 min-h-0 gap-1.5 px-3 py-1 text-xs"
                      onClick={() => setAddProfileOpen(true)}
                    >
                      <Plus className="h-3.5 w-3.5" /> Add staff member
                    </button>
                  )}
                </div>
              </div>
              {isExporting && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                  <Download className="h-3.5 w-3.5 animate-bounce" /> Preparing the filtered staff directory export…
                </p>
              )}
            </div>
            <div className="card-content space-y-4">
              <div className="filter-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div role="group" aria-label="Employment status" className="segmented">
                    {STAFF_STATUS_TABS.map(([value, label]) => (
                      <button
                        key={label}
                        type="button"
                        aria-pressed={staffFilters.status === value}
                        onClick={() => updateStaffFilters({ status: value })}
                        className="segmented-option"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {activeStaffFilters > 0 && (
                    <button
                      type="button"
                      onClick={() => setStaffFilters(EMPTY_STAFF_FILTERS)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                    >
                      <X className="h-3.5 w-3.5" /> Clear {activeStaffFilters}{" "}
                      {activeStaffFilters === 1 ? "filter" : "filters"}
                    </button>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(16rem,2fr)_repeat(3,minmax(0,1fr))]">
                  <label className="filter-field sm:col-span-2 xl:col-span-1">
                    Search
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="search"
                        value={staffFilters.search}
                        onChange={(event) => updateStaffFilters({ search: event.target.value })}
                        className="input-compact pl-8"
                        placeholder="Name, username, employee ID, position or location"
                      />
                    </div>
                  </label>
                  <label className="filter-field">
                    Department
                    <select
                      value={staffFilters.department}
                      onChange={(event) => updateStaffFilters({ department: event.target.value })}
                      className="input-compact"
                    >
                      <option value="">All departments</option>
                      <option value="none">School-wide (no department)</option>
                      {departments.data?.map((department) => (
                        <option key={department.id} value={department.id}>
                          {department.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="filter-field">
                    Position
                    <select
                      value={staffFilters.position}
                      onChange={(event) => updateStaffFilters({ position: event.target.value })}
                      className="input-compact"
                    >
                      <option value="">All positions</option>
                      {positions.data?.map((position) => (
                        <option key={position.id} value={position.id}>
                          {position.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="filter-field">
                    Pay
                    <select
                      value={staffFilters.pay}
                      onChange={(event) =>
                        updateStaffFilters({ pay: event.target.value as StaffDirectoryFilters["pay"] })
                      }
                      className="input-compact"
                    >
                      <option value="">Any</option>
                      <option value="set">Pay set</option>
                      <option value="unset">Pay not set yet</option>
                    </select>
                  </label>
                </div>
              </div>

              {profiles.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />
                  ))}
                </div>
              ) : visibleProfiles.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                  {activeStaffFilters
                    ? "No staff match these filters. Clear a filter or try another search."
                    : "No staff profiles yet."}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="sheet-wrap hidden md:block">
                    <table className="sheet">
                      <thead>
                        <tr>
                          <th className="sheet-rownum">#</th>
                          <th>{sortButton("Name", "user_name")}</th>
                          <th>Username</th>
                          <th>{sortButton("Employee ID", "employee_id")}</th>
                          <th>{sortButton("Position", "position_title")}</th>
                          <th>{sortButton("Department", "department_name")}</th>
                          <th>{sortButton("Hired", "hire_date")}</th>
                          <th>{sortButton("Status", "employment_status")}</th>
                          <th className="text-right">{sortButton("Salary", "salary")}</th>
                          {canManage && <th className="text-right">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {pagedProfiles.map((p, index) => (
                          <tr key={p.id} className="cursor-pointer" onClick={() => setViewingProfile(p)}>
                            <td className="sheet-rownum">{firstProfileRow + index}</td>
                            <td>
                              <span className="font-medium text-gray-900">{p.user_name}</span>
                              {headedDepartmentsBadges(p.headed_departments)}
                            </td>
                            <td className="font-mono text-xs text-gray-600">{p.user_username}</td>
                            <td className="font-mono text-xs text-gray-700">{p.employee_id}</td>
                            <td className="max-w-[12rem] truncate text-gray-700" title={p.position_title}>
                              {p.position_title}
                            </td>
                            <td className="max-w-[12rem] truncate text-gray-700">
                              {p.department_name || <span className="italic text-gray-400">School-wide</span>}
                            </td>
                            <td className="tabular-nums text-xs text-gray-600">{formatSchoolDate(p.hire_date)}</td>
                            <td>{statusDot(p.employment_status)}</td>
                            <td className="text-right tabular-nums">
                              {p.salary ? (
                                <span className="text-gray-900">
                                  <span className="text-[11px] text-gray-400">{p.currency}</span>{" "}
                                  {Number(p.salary).toLocaleString()}
                                </span>
                              ) : (
                                <span className="text-xs italic text-gray-400">Not set</span>
                              )}
                            </td>
                            {canManage && (
                              <td onClick={(event) => event.stopPropagation()}>
                                <div className="flex items-center justify-end gap-0.5">
                                  <button
                                    type="button"
                                    className="sheet-action text-primary-700"
                                    title="Edit employment"
                                    aria-label={`Edit employment for ${p.user_name}`}
                                    onClick={() => openEditProfile(p)}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    className="sheet-action text-emerald-700"
                                    title={p.salary ? "Request pay change" : "Set pay for approval"}
                                    aria-label={`${p.salary ? "Request pay change" : "Set pay"} for ${p.user_name}`}
                                    onClick={() => openPayChange(p)}
                                  >
                                    <WalletCards className="h-3.5 w-3.5" />
                                  </button>
                                  {["active", "probation", "suspended"].includes(p.employment_status) && (
                                    <button
                                      type="button"
                                      className="sheet-action text-red-600"
                                      title="End employment"
                                      aria-label={`End employment for ${p.user_name}`}
                                      onClick={() => openOffboarding(p.id)}
                                    >
                                      <UserMinus className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="grid gap-3 md:hidden">{pagedProfiles.map((p) => <article key={p.id} className="card cursor-pointer" onClick={() => setViewingProfile(p)}><div className="card-content"><div className="flex items-start justify-between gap-3"><div><h3 className="font-semibold">{p.user_name}</h3><p className="text-sm text-gray-500">@{p.user_username} · {p.employee_id}</p>{headedDepartmentsBadges(p.headed_departments)}</div><StaffStatus status={p.employment_status} /></div><div className="mt-3 grid grid-cols-2 gap-2 text-sm text-gray-600"><span>{p.position_title}</span><span>{p.department_name || "School-wide"}</span><span>Hired {formatSchoolDate(p.hire_date)}</span><span>{p.salary ? `${p.currency} ${Number(p.salary).toLocaleString()}` : <span className="text-gray-400">Not set</span>}</span></div>{canManage && <div className="mt-3 flex flex-wrap gap-3 border-t pt-3" onClick={(event) => event.stopPropagation()}><button type="button" className="text-sm text-primary-700" onClick={() => openEditProfile(p)}>Edit employment</button><button type="button" className="text-sm text-primary-700" onClick={() => openPayChange(p)}>Pay approval</button>{["active", "probation", "suspended"].includes(p.employment_status) && <button type="button" className="text-sm text-red-700" onClick={() => openOffboarding(p.id)}>End employment</button>}</div>}</div></article>)}</div>
                  <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600">
                    <div className="flex flex-wrap items-center gap-4">
                      <span>
                        Showing{" "}
                        <strong className="font-semibold text-gray-900">
                          {firstProfileRow}–{firstProfileRow + pagedProfiles.length - 1}
                        </strong>{" "}
                        of <strong className="font-semibold text-gray-900">{visibleProfiles.length}</strong> staff
                      </span>
                      <label className="inline-flex items-center gap-1.5">
                        Rows per page
                        <select
                          value={profilePageSize}
                          onChange={(event) => setProfilePageSize(Number(event.target.value))}
                          className="h-7 rounded border border-gray-300 bg-white px-1.5 text-xs"
                        >
                          {STAFF_DIRECTORY_PAGE_SIZES.map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="pager-button"
                        disabled={profilePage === 1}
                        onClick={() => setProfilePage((page) => page - 1)}
                      >
                        Previous
                      </button>
                      <span>
                        Page {profilePage} of {profilePageCount}
                      </span>
                      <button
                        type="button"
                        className="pager-button"
                        disabled={profilePage === profilePageCount}
                        onClick={() => setProfilePage((page) => page + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {viewingProfile && (
        <StaffProfileModal
          profile={viewingProfile}
          canManage={canManage}
          onEdit={() => {
            openEditProfile(viewingProfile);
            setViewingProfile(null);
          }}
          onPayChange={() => {
            openPayChange(viewingProfile);
            setViewingProfile(null);
          }}
          onClose={() => setViewingProfile(null)}
        />
      )}
      {payChange && (
        <PayChangeModal
          target={payChange}
          pending={requestSalaryChange.isPending || reviseSalaryChange.isPending}
          error={payChange.requestId ? reviseSalaryChange.error : requestSalaryChange.error}
          onSubmit={submitPayChange}
          onClose={() => setPayChange(null)}
        />
      )}
      {tab === "salary" && (
        <div className="card">
          <div className="card-header pb-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <WalletCards className="h-5 w-5" /> Pay change approvals
                </h2>
                <p className="max-w-2xl text-sm text-gray-500">
                  Basic pay and allowances change together on one approved, effective-dated
                  request. Payroll uses whatever was approved and in force at the period end.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-gray-500">Export</span>
                {(
                  [
                    ["csv", FileText, "CSV"],
                    ["excel", FileSpreadsheet, "Excel"],
                    ["pdf", Printer, "PDF"],
                  ] as const
                ).map(([format, Icon, label]) => (
                  <button
                    key={format}
                    type="button"
                    disabled={!visiblePayChanges.length}
                    onClick={() => exportSalaryChanges(format)}
                    className="pager-button h-8"
                    title={`Download the filtered pay changes as ${label}`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="card-content space-y-4">
            <div className="filter-panel">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div role="group" aria-label="Request status" className="segmented">
                  {PAY_CHANGE_STATUS_TABS.map(([value, label]) => (
                    <button
                      key={label}
                      type="button"
                      aria-pressed={payFilters.status === value}
                      onClick={() => updatePayFilters({ status: value })}
                      className="segmented-option"
                    >
                      {label}
                      <span className="ml-1.5 tabular-nums opacity-70">{payStatusCounts[value] ?? 0}</span>
                    </button>
                  ))}
                </div>
                {activePayFilters > 0 && (
                  <button
                    type="button"
                    onClick={() => setPayFilters(EMPTY_PAY_CHANGE_FILTERS)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                  >
                    <X className="h-3.5 w-3.5" /> Clear {activePayFilters}{" "}
                    {activePayFilters === 1 ? "filter" : "filters"}
                  </button>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(16rem,2fr)_repeat(3,minmax(0,1fr))]">
                <label className="filter-field sm:col-span-2 xl:col-span-1">
                  Search
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="search"
                      value={payFilters.search}
                      onChange={(event) => updatePayFilters({ search: event.target.value })}
                      className="input-compact pl-8"
                      placeholder="Staff member, reason, or who requested or decided"
                    />
                  </div>
                </label>
                <label className="filter-field">
                  Department
                  <select
                    value={payFilters.department}
                    onChange={(event) => updatePayFilters({ department: event.target.value })}
                    className="input-compact"
                  >
                    <option value="">All departments</option>
                    <option value="none">School-wide (no department)</option>
                    {departments.data?.map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="filter-field">
                  Effective from
                  <input
                    type="date"
                    value={payFilters.from}
                    max={payFilters.to || undefined}
                    onChange={(event) => updatePayFilters({ from: event.target.value })}
                    className="input-compact"
                  />
                </label>
                <label className="filter-field">
                  Effective to
                  <input
                    type="date"
                    value={payFilters.to}
                    min={payFilters.from || undefined}
                    onChange={(event) => updatePayFilters({ to: event.target.value })}
                    className="input-compact"
                  />
                </label>
              </div>
            </div>

            {salaryChanges.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />
                ))}
              </div>
            ) : !visiblePayChanges.length ? (
              <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                {activePayFilters
                  ? "No pay changes match these filters."
                  : "No pay changes yet. Raise one from a staff member's record in the Directory."}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="sheet-wrap">
                  <table className="sheet">
                    <thead>
                      <tr>
                        <th className="sheet-rownum">#</th>
                        <th>Staff member</th>
                        <th>Effective</th>
                        <th className="text-right">Basic pay</th>
                        <th className="text-right">Allowances</th>
                        <th className="text-right">Gross change</th>
                        <th>Reason</th>
                        <th>Requested by</th>
                        <th>Status</th>
                        <th className="text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visiblePayChanges.map((change, index) => {
                        const gross = grossChange(change);
                        const allowancesBefore = totalOf(change.previous_allowances ?? []);
                        const allowancesAfter = totalOf(change.proposed_allowances ?? []);
                        const awaiting = canApproveSalary && change.workflow_status === "submitted";
                        const revisable =
                          canManage && change.workflow_status === "returned" && change.initiated_by === user?.id;
                        return (
                          <tr key={change.id} className="cursor-pointer" onClick={() => openPayReview(change)}>
                            <td className="sheet-rownum">{index + 1}</td>
                            <td className="font-medium text-gray-900">{change.staff_name}</td>
                            <td className="tabular-nums text-xs text-gray-600">{formatSchoolDate(change.effective_date)}</td>
                            <td className="text-right tabular-nums">
                              <span className="text-gray-400">{Number(change.previous_salary).toLocaleString()} →</span>{" "}
                              <span className="text-gray-900">{Number(change.proposed_salary).toLocaleString()}</span>
                            </td>
                            <td
                              className="text-right tabular-nums"
                              title={
                                (change.proposed_allowances ?? [])
                                  .map((line) => `${line.name} ${Number(line.amount).toLocaleString()}`)
                                  .join(", ") || "No allowances"
                              }
                            >
                              {allowancesBefore === allowancesAfter ? (
                                <span className="text-gray-500">{allowancesAfter.toLocaleString()}</span>
                              ) : (
                                <>
                                  <span className="text-gray-400">{allowancesBefore.toLocaleString()} →</span>{" "}
                                  <span className="text-gray-900">{allowancesAfter.toLocaleString()}</span>
                                </>
                              )}
                            </td>
                            <td
                              className={`text-right tabular-nums font-medium ${gross.difference > 0 ? "text-green-700" : gross.difference < 0 ? "text-red-700" : "text-gray-400"}`}
                            >
                              {describeChange(gross.before, gross.after)}
                            </td>
                            <td className="max-w-[16rem] truncate text-gray-600" title={change.reason}>
                              {change.reason}
                            </td>
                            <td className="text-gray-600">{change.initiated_by_name}</td>
                            <td>
                              <PayChangeStatus status={change.workflow_status} />
                            </td>
                            <td className="text-right">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openPayReview(change);
                                }}
                                className={`rounded px-2 py-0.5 text-xs font-medium ${
                                  awaiting
                                    ? "bg-primary-600 text-white hover:bg-primary-700"
                                    : revisable
                                      ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                                      : "text-primary-700 hover:bg-primary-50"
                                }`}
                              >
                                {awaiting ? "Review" : revisable ? "Revise" : "View"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500">
                  {visiblePayChanges.length} of {salaryChanges.data?.length ?? 0} requests · awaiting approval
                  first, then latest effective date.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {reviewingPayChange && (
        <PayChangeReviewModal
          key={reviewingPayChange.id}
          change={reviewingPayChange}
          canDecide={canApproveSalary}
          canRevise={
            canManage &&
            reviewingPayChange.workflow_status === "returned" &&
            reviewingPayChange.initiated_by === user?.id
          }
          busy={approveSalaryChange.isPending || returnSalaryChange.isPending || rejectSalaryChange.isPending}
          error={approveSalaryChange.error || returnSalaryChange.error || rejectSalaryChange.error}
          onApprove={() =>
            approveSalaryChange.mutate(reviewingPayChange.id, { onSuccess: () => setReviewingPayChangeId(null) })
          }
          onReturn={(reason) =>
            returnSalaryChange.mutate(
              { id: reviewingPayChange.id, reason },
              { onSuccess: () => setReviewingPayChangeId(null) },
            )
          }
          onReject={(reason) =>
            rejectSalaryChange.mutate(
              { id: reviewingPayChange.id, reason },
              { onSuccess: () => setReviewingPayChangeId(null) },
            )
          }
          onRevise={() => {
            const change = reviewingPayChange;
            setReviewingPayChangeId(null);
            openRevision(change);
          }}
          onClose={() => setReviewingPayChangeId(null)}
        />
      )}
      {tab === "leave" && (
        <div className="card">
          <div className="card-header pb-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Calendar className="h-5 w-5" /> Leave register
                </h2>
                <p className="text-sm text-gray-500">
                  Requests, approvals and who is away, with each person's yearly allowance.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-gray-500">Export</span>
                {(
                  [
                    ["csv", FileText, "CSV"],
                    ["excel", FileSpreadsheet, "Excel"],
                    ["pdf", Printer, "PDF"],
                  ] as const
                ).map(([format, Icon, label]) => (
                  <button
                    key={format}
                    type="button"
                    disabled={!visibleLeaves.length}
                    onClick={() => exportLeave(format)}
                    className="pager-button h-8"
                    title={`Download the filtered leave register as ${label}`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn btn-primary ml-1 h-8 min-h-0 gap-1.5 px-3 py-1 text-xs"
                  onClick={() => {
                    requestLeave.reset();
                    setRequestingLeave(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" /> Request leave
                </button>
              </div>
            </div>
          </div>
          <div className="card-content space-y-4">
            <div className="filter-panel">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div role="group" aria-label="Leave status" className="segmented">
                  {LEAVE_STATUS_TABS.map(([value, label]) => (
                    <button
                      key={label}
                      type="button"
                      aria-pressed={leaveFilters.status === value}
                      onClick={() => updateLeaveFilters({ status: value })}
                      className="segmented-option"
                    >
                      {label}
                      <span className="ml-1.5 tabular-nums opacity-70">{leaveCounts[value] ?? 0}</span>
                    </button>
                  ))}
                </div>
                {activeLeaveFilters > 0 && (
                  <button
                    type="button"
                    onClick={() => setLeaveFilters(EMPTY_LEAVE_FILTERS)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                  >
                    <X className="h-3.5 w-3.5" /> Clear {activeLeaveFilters}{" "}
                    {activeLeaveFilters === 1 ? "filter" : "filters"}
                  </button>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[minmax(16rem,2fr)_repeat(4,minmax(0,1fr))]">
                <label className="filter-field sm:col-span-2 lg:col-span-3 2xl:col-span-1">
                  Search
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="search"
                      value={leaveFilters.search}
                      onChange={(event) => updateLeaveFilters({ search: event.target.value })}
                      className="input-compact pl-8"
                      placeholder="Staff member, reason or approver"
                    />
                  </div>
                </label>
                <label className="filter-field">
                  Leave type
                  <select
                    value={leaveFilters.leaveType}
                    onChange={(event) => updateLeaveFilters({ leaveType: event.target.value })}
                    className="input-compact"
                  >
                    <option value="">All types</option>
                    {leaveTypes.data?.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </label>
                {canManage && (
                  <label className="filter-field">
                    Department
                    <select
                      value={leaveFilters.department}
                      onChange={(event) => updateLeaveFilters({ department: event.target.value })}
                      className="input-compact"
                    >
                      <option value="">All departments</option>
                      <option value="none">School-wide (no department)</option>
                      {departments.data?.map((department) => (
                        <option key={department.id} value={department.id}>
                          {department.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="filter-field">
                  Away between
                  <input
                    type="date"
                    value={leaveFilters.from}
                    max={leaveFilters.to || undefined}
                    onChange={(event) => updateLeaveFilters({ from: event.target.value })}
                    className="input-compact"
                  />
                </label>
                <label className="filter-field">
                  and
                  <input
                    type="date"
                    value={leaveFilters.to}
                    min={leaveFilters.from || undefined}
                    onChange={(event) => updateLeaveFilters({ to: event.target.value })}
                    className="input-compact"
                  />
                </label>
              </div>
            </div>

            {leaves.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />
                ))}
              </div>
            ) : !visibleLeaves.length ? (
              <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                {activeLeaveFilters ? "No leave matches these filters." : "No leave has been requested yet."}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="sheet-wrap">
                  <table className="sheet">
                    <thead>
                      <tr>
                        <th className="sheet-rownum">#</th>
                        <th>Staff member</th>
                        <th>Leave type</th>
                        <th>From</th>
                        <th>To</th>
                        <th className="text-right">Days</th>
                        <th>Reason</th>
                        <th>Status</th>
                        <th>Decided by</th>
                        <th className="text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedLeaves.map((leave, index) => {
                        const reviewable = canManage && leave.status === "pending";
                        return (
                          <tr key={leave.id} className="cursor-pointer" onClick={() => openLeaveReview(leave)}>
                            <td className="sheet-rownum">{firstLeaveRow + index}</td>
                            <td className="font-medium text-gray-900">{leave.staff_name}</td>
                            <td className="text-gray-700">{leave.leave_type_name}</td>
                            <td className="tabular-nums text-xs text-gray-600">{formatSchoolDate(leave.start_date)}</td>
                            <td className="tabular-nums text-xs text-gray-600">{formatSchoolDate(leave.end_date)}</td>
                            <td className="text-right tabular-nums">{leave.total_days}</td>
                            <td className="max-w-[16rem] truncate text-gray-600" title={leave.reason}>
                              {leave.reason}
                            </td>
                            <td>
                              <LeaveStatus status={leave.status} />
                            </td>
                            <td className="text-gray-600" title={leave.approval_notes || undefined}>
                              {leave.approved_by_name || <span className="text-gray-300">—</span>}
                            </td>
                            <td className="text-right">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openLeaveReview(leave);
                                }}
                                className={`rounded px-2 py-0.5 text-xs font-medium ${
                                  reviewable
                                    ? "bg-primary-600 text-white hover:bg-primary-700"
                                    : "text-primary-700 hover:bg-primary-50"
                                }`}
                              >
                                {reviewable ? "Review" : "View"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600">
                  <div className="flex flex-wrap items-center gap-4">
                    <span>
                      Showing{" "}
                      <strong className="font-semibold text-gray-900">
                        {firstLeaveRow}–{firstLeaveRow + pagedLeaves.length - 1}
                      </strong>{" "}
                      of <strong className="font-semibold text-gray-900">{visibleLeaves.length}</strong> · pending first
                    </span>
                    <label className="inline-flex items-center gap-1.5">
                      Rows per page
                      <select
                        value={leavePageSize}
                        onChange={(event) => {
                          setLeavePageSize(Number(event.target.value));
                          setLeavePage(1);
                        }}
                        className="h-7 rounded border border-gray-300 bg-white px-1.5 text-xs"
                      >
                        {STAFF_DIRECTORY_PAGE_SIZES.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="pager-button"
                      disabled={leavePage === 1}
                      onClick={() => setLeavePage((page) => page - 1)}
                    >
                      Previous
                    </button>
                    <span>
                      Page {leavePage} of {leavePageCount}
                    </span>
                    <button
                      type="button"
                      className="pager-button"
                      disabled={leavePage === leavePageCount}
                      onClick={() => setLeavePage((page) => page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {requestingLeave && (
        <LeaveRequestModal
          canManage={canManage}
          profiles={profiles.data ?? []}
          leaveTypes={leaveTypes.data ?? []}
          leaves={leaves.data ?? []}
          ownProfileId={ownProfileId}
          isSaving={requestLeave.isPending}
          error={requestLeave.error}
          onSubmit={(values) => requestLeave.mutate(values, { onSuccess: () => setRequestingLeave(false) })}
          onClose={() => setRequestingLeave(false)}
        />
      )}
      {reviewingLeave && (
        <LeaveReviewModal
          key={reviewingLeave.id}
          leave={reviewingLeave}
          leaves={leaves.data ?? []}
          leaveTypes={leaveTypes.data ?? []}
          profiles={profiles.data ?? []}
          canDecide={canManage}
          canCancel={reviewingLeave.staff === ownProfileId}
          busy={approve.isPending || reject.isPending || cancel.isPending}
          error={approve.error || reject.error || cancel.error}
          onApprove={(notes) =>
            approve.mutate({ id: reviewingLeave.id, notes }, { onSuccess: () => setReviewingLeaveId(null) })
          }
          onReject={(notes) =>
            reject.mutate({ id: reviewingLeave.id, notes }, { onSuccess: () => setReviewingLeaveId(null) })
          }
          onCancelRequest={() => cancel.mutate(reviewingLeave.id, { onSuccess: () => setReviewingLeaveId(null) })}
          onClose={() => setReviewingLeaveId(null)}
        />
      )}
      {tab === "attendance" && (
        <div className="card">
          <div className="card-header pb-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <CalendarCheck className="h-5 w-5" /> Staff attendance
                </h2>
                <p className="text-sm text-gray-500">
                  Daily records. Appraisals read these as attendance evidence.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-gray-500">Export</span>
                {(
                  [
                    ["csv", FileText, "CSV"],
                    ["excel", FileSpreadsheet, "Excel"],
                    ["pdf", Printer, "PDF"],
                  ] as const
                ).map(([format, Icon, label]) => (
                  <button
                    key={format}
                    type="button"
                    disabled={!visibleAttendance.length}
                    onClick={() => exportAttendance(format)}
                    className="pager-button h-8"
                    title={`Download the filtered attendance as ${label}`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
                {canManage && (
                  <button
                    type="button"
                    className="btn btn-primary ml-1 h-8 min-h-0 gap-1.5 px-3 py-1 text-xs"
                    onClick={() => {
                      addAttendance.reset();
                      setRecordingAttendance(true);
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" /> Record attendance
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="card-content space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-900">
              <span>Performance reviews now live in Appraisals, with templates, evidence and approval.</span>
              <Link to="/appraisals" className="font-medium underline">
                Open Appraisals
              </Link>
            </div>

            <div className="filter-panel">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div role="group" aria-label="Attendance status" className="segmented">
                  {ATTENDANCE_STATUS_TABS.map(([value, label]) => (
                    <button
                      key={label}
                      type="button"
                      aria-pressed={attendanceFilters.status === value}
                      onClick={() => updateAttendanceFilters({ status: value })}
                      className="segmented-option"
                    >
                      {label}
                      <span className="ml-1.5 tabular-nums opacity-70">{attendanceCounts[value] ?? 0}</span>
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <button
                    type="button"
                    onClick={() => updateAttendanceFilters({ from: localToday(), to: localToday() })}
                    className="font-medium text-primary-700 hover:underline"
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={() => updateAttendanceFilters({ from: weekStart(), to: localToday() })}
                    className="font-medium text-primary-700 hover:underline"
                  >
                    This week
                  </button>
                  {activeAttendanceFilters > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setAttendanceFilters(EMPTY_ATTENDANCE_FILTERS);
                        setAttendancePage(1);
                      }}
                      className="inline-flex items-center gap-1 font-medium text-primary-700 hover:underline"
                    >
                      <X className="h-3.5 w-3.5" /> Clear {activeAttendanceFilters}{" "}
                      {activeAttendanceFilters === 1 ? "filter" : "filters"}
                    </button>
                  )}
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(16rem,2fr)_repeat(3,minmax(0,1fr))]">
                <label className="filter-field sm:col-span-2 xl:col-span-1">
                  Search
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="search"
                      value={attendanceFilters.search}
                      onChange={(event) => updateAttendanceFilters({ search: event.target.value })}
                      className="input-compact pl-8"
                      placeholder="Staff member or notes"
                    />
                  </div>
                </label>
                <label className="filter-field">
                  Department
                  <select
                    value={attendanceFilters.department}
                    onChange={(event) => updateAttendanceFilters({ department: event.target.value })}
                    className="input-compact"
                  >
                    <option value="">All departments</option>
                    <option value="none">School-wide (no department)</option>
                    {departments.data?.map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="filter-field">
                  From
                  <input
                    type="date"
                    value={attendanceFilters.from}
                    max={attendanceFilters.to || undefined}
                    onChange={(event) => updateAttendanceFilters({ from: event.target.value })}
                    className="input-compact"
                  />
                </label>
                <label className="filter-field">
                  To
                  <input
                    type="date"
                    value={attendanceFilters.to}
                    min={attendanceFilters.from || undefined}
                    onChange={(event) => updateAttendanceFilters({ to: event.target.value })}
                    className="input-compact"
                  />
                </label>
              </div>
            </div>

            {attendance.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />
                ))}
              </div>
            ) : !visibleAttendance.length ? (
              <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                {activeAttendanceFilters ? "No attendance matches these filters." : "No attendance recorded yet."}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="sheet-wrap">
                  <table className="sheet">
                    <thead>
                      <tr>
                        <th className="sheet-rownum">#</th>
                        <th>Date</th>
                        <th>Staff member</th>
                        <th>Department</th>
                        <th>Status</th>
                        <th>In</th>
                        <th>Out</th>
                        <th className="text-right">Hours</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedAttendance.map((record, index) => {
                        const profile = profileById.get(record.staff);
                        return (
                          <tr key={record.id}>
                            <td className="sheet-rownum">{firstAttendanceRow + index}</td>
                            <td className="tabular-nums text-xs text-gray-600">{formatSchoolDate(record.date)}</td>
                            <td className="font-medium text-gray-900">{record.staff_name}</td>
                            <td className="text-gray-600">
                              {profile?.department_name || <span className="italic text-gray-400">School-wide</span>}
                            </td>
                            <td>
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700">
                                <span
                                  className={`h-1.5 w-1.5 rounded-full ${ATTENDANCE_STATUS_DOTS[record.status] || "bg-gray-400"}`}
                                />
                                {ATTENDANCE_STATUS_TABS.find(([value]) => value === record.status)?.[1] ?? record.status}
                              </span>
                            </td>
                            <td className="tabular-nums text-gray-600">
                              {shortTime(record.check_in_time) || <span className="text-gray-300">—</span>}
                            </td>
                            <td className="tabular-nums text-gray-600">
                              {shortTime(record.check_out_time) || <span className="text-gray-300">—</span>}
                            </td>
                            <td className="text-right tabular-nums">
                              {Number(record.hours_worked) > 0 ? Number(record.hours_worked) : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="max-w-[18rem] truncate text-gray-600" title={record.notes || undefined}>
                              {record.notes || <span className="text-gray-300">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600">
                  <span>
                    Showing{" "}
                    <strong className="font-semibold text-gray-900">
                      {firstAttendanceRow}–{firstAttendanceRow + pagedAttendance.length - 1}
                    </strong>{" "}
                    of <strong className="font-semibold text-gray-900">{visibleAttendance.length}</strong> records ·
                    25 per page
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="pager-button"
                      disabled={attendancePage === 1}
                      onClick={() => setAttendancePage((page) => page - 1)}
                    >
                      Previous
                    </button>
                    <span>
                      Page {attendancePage} of {attendancePageCount}
                    </span>
                    <button
                      type="button"
                      className="pager-button"
                      disabled={attendancePage === attendancePageCount}
                      onClick={() => setAttendancePage((page) => page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {recordingAttendance && (
        <RecordAttendanceModal
          profiles={profiles.data ?? []}
          attendance={attendance.data ?? []}
          leaves={leaves.data ?? []}
          isSaving={addAttendance.isPending}
          error={addAttendance.error}
          onSubmit={(values) => addAttendance.mutate(values, { onSuccess: () => setRecordingAttendance(false) })}
          onClose={() => setRecordingAttendance(false)}
        />
      )}
      {tab === "training" && (
        <div className="card">
          <div className="card-header pb-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <GraduationCap className="h-5 w-5" /> Training & development
                </h2>
                <p className="text-sm text-gray-500">
                  Scheduled sessions, who attended, and who still has mandatory training to do.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div role="group" aria-label="Training view" className="segmented mr-1">
                  {(
                    [
                      ["sessions", "Sessions"],
                      ["register", "Completion register"],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={trainingView === value}
                      onClick={() => setTrainingView(value)}
                      className="segmented-option"
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <span className="text-xs font-medium text-gray-500">Export</span>
                {(
                  [
                    ["csv", FileText, "CSV"],
                    ["excel", FileSpreadsheet, "Excel"],
                    ["pdf", Printer, "PDF"],
                  ] as const
                ).map(([format, Icon, label]) => (
                  <button
                    key={format}
                    type="button"
                    disabled={trainingView === "sessions" ? !visibleTraining.length : !visibleCompletions.length}
                    onClick={() =>
                      trainingView === "sessions" ? exportTraining(format) : exportTrainingAttendance(format)
                    }
                    className="pager-button h-8"
                    title={`Download the filtered ${trainingView === "sessions" ? "sessions" : "completion register"} as ${label}`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
                {canManage && (
                  <button
                    type="button"
                    className="btn btn-primary ml-1 h-8 min-h-0 gap-1.5 px-3 py-1 text-xs"
                    onClick={() => {
                      addTraining.reset();
                      setSchedulingTraining(true);
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" /> Schedule training
                  </button>
                )}
              </div>
            </div>
          </div>

          {trainingView === "sessions" ? (
            <div className="card-content space-y-4">
              <div className="filter-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div role="group" aria-label="Session timing" className="segmented">
                    {TRAINING_PHASE_TABS.map(([value, label]) => (
                      <button
                        key={label}
                        type="button"
                        aria-pressed={trainingFilters.phase === value}
                        onClick={() => updateTrainingFilters({ phase: value })}
                        className="segmented-option"
                      >
                        {label}
                        <span className="ml-1.5 tabular-nums opacity-70">{trainingCounts[value] ?? 0}</span>
                      </button>
                    ))}
                  </div>
                  {activeTrainingFilters > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setTrainingFilters(EMPTY_TRAINING_FILTERS);
                        setTrainingPage(1);
                      }}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                    >
                      <X className="h-3.5 w-3.5" /> Clear {activeTrainingFilters}{" "}
                      {activeTrainingFilters === 1 ? "filter" : "filters"}
                    </button>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(16rem,2fr)_repeat(2,minmax(0,1fr))]">
                  <label className="filter-field sm:col-span-2 xl:col-span-1">
                    Search
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="search"
                        value={trainingFilters.search}
                        onChange={(event) => updateTrainingFilters({ search: event.target.value })}
                        className="input-compact pl-8"
                        placeholder="Training, trainer, venue or organiser"
                      />
                    </div>
                  </label>
                  <label className="filter-field">
                    Type
                    <select
                      value={trainingFilters.type}
                      onChange={(event) => updateTrainingFilters({ type: event.target.value })}
                      className="input-compact"
                    >
                      <option value="">All types</option>
                      {TRAINING_TYPES.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="filter-field">
                    Requirement
                    <select
                      value={trainingFilters.mandatory}
                      onChange={(event) =>
                        updateTrainingFilters({ mandatory: event.target.value as TrainingFilters["mandatory"] })
                      }
                      className="input-compact"
                    >
                      <option value="">Mandatory and optional</option>
                      <option value="yes">Mandatory only</option>
                      <option value="no">Optional only</option>
                    </select>
                  </label>
                </div>
              </div>

              {training.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />
                  ))}
                </div>
              ) : !visibleTraining.length ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                  {activeTrainingFilters ? "No sessions match these filters." : "No training has been scheduled yet."}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="sheet-wrap">
                    <table className="sheet">
                      <thead>
                        <tr>
                          <th className="sheet-rownum">#</th>
                          <th>Training</th>
                          <th>Type</th>
                          <th>Dates</th>
                          <th className="text-right">Hours</th>
                          <th>Venue</th>
                          <th>Trainer</th>
                          <th className="text-right">Attended</th>
                          <th className="text-right">Passed</th>
                          <th>Timing</th>
                          <th className="text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedTraining.map((session, index) => {
                          const stats = sessionStats(session.id, trainingAttendance.data ?? []);
                          return (
                            <tr key={session.id} className="cursor-pointer" onClick={() => setViewingTrainingId(session.id)}>
                              <td className="sheet-rownum">{firstTrainingRow + index}</td>
                              <td className="max-w-[18rem] truncate" title={session.name}>
                                <span className="font-medium text-gray-900">{session.name}</span>
                                {session.is_mandatory && (
                                  <span className="ml-1.5 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700">
                                    Mandatory
                                  </span>
                                )}
                              </td>
                              <td className="text-gray-600">{trainingTypeLabel(session.training_type)}</td>
                              <td className="tabular-nums text-xs text-gray-600">
                                {formatSchoolDate(session.start_date)}
                                {session.end_date !== session.start_date && ` – ${formatSchoolDate(session.end_date)}`}
                              </td>
                              <td className="text-right tabular-nums">{session.duration_hours}</td>
                              <td className="max-w-[10rem] truncate text-gray-600">
                                {session.venue || <span className="text-gray-300">—</span>}
                              </td>
                              <td className="max-w-[10rem] truncate text-gray-600">
                                {session.trainer || <span className="text-gray-300">—</span>}
                              </td>
                              <td className="text-right tabular-nums">{stats.attended}</td>
                              <td className="text-right tabular-nums">
                                {stats.attended ? (
                                  <>
                                    {stats.passed}{" "}
                                    <span className="text-[11px] text-gray-400">
                                      ({Math.round((stats.passed / stats.attended) * 100)}%)
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                              <td>
                                <TrainingPhaseLabel training={session} />
                              </td>
                              <td className="text-right">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setViewingTrainingId(session.id);
                                  }}
                                  className="rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                                >
                                  {canManage ? "Results" : "View"}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <Pager
                    first={firstTrainingRow}
                    shown={pagedTraining.length}
                    total={visibleTraining.length}
                    noun="sessions"
                    page={trainingPage}
                    pageCount={trainingPageCount}
                    onPage={setTrainingPage}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="card-content space-y-4">
              <div className="filter-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div role="group" aria-label="Result" className="segmented">
                    {COMPLETION_RESULT_TABS.map(([value, label]) => (
                      <button
                        key={label}
                        type="button"
                        aria-pressed={completionResult === value}
                        onClick={() => {
                          setCompletionResult(value);
                          setCompletionPage(1);
                        }}
                        className="segmented-option"
                      >
                        {label}
                        <span className="ml-1.5 tabular-nums opacity-70">{completionCounts[value] ?? 0}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <label className="filter-field block sm:max-w-md">
                  Search
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="search"
                      value={completionSearch}
                      onChange={(event) => {
                        setCompletionSearch(event.target.value);
                        setCompletionPage(1);
                      }}
                      className="input-compact pl-8"
                      placeholder="Staff member or training"
                    />
                  </div>
                </label>
              </div>

              {!visibleCompletions.length ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                  {completionSearch || completionResult
                    ? "No results match these filters."
                    : "No training results recorded yet. Open a session to record them."}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="sheet-wrap">
                    <table className="sheet">
                      <thead>
                        <tr>
                          <th className="sheet-rownum">#</th>
                          <th>Staff member</th>
                          <th>Training</th>
                          <th>Completed</th>
                          <th>Attended</th>
                          <th className="text-right">Score</th>
                          <th>Result</th>
                          <th>Feedback</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedCompletions.map((record, index) => (
                          <tr key={record.id} className="cursor-pointer" onClick={() => setViewingTrainingId(record.training)}>
                            <td className="sheet-rownum">{firstCompletionRow + index}</td>
                            <td className="font-medium text-gray-900">{record.staff_name}</td>
                            <td className="max-w-[16rem] truncate text-gray-700" title={record.training_name}>
                              {record.training_name}
                            </td>
                            <td className="tabular-nums text-xs text-gray-600">
                              {record.completion_date ? formatSchoolDate(record.completion_date) : <span className="text-gray-300">—</span>}
                            </td>
                            <td>{record.attended ? "Yes" : <span className="text-red-700">No</span>}</td>
                            <td className="text-right tabular-nums">
                              {record.score != null && record.score !== "" ? `${Number(record.score)}%` : <span className="text-gray-300">—</span>}
                            </td>
                            <td>
                              {record.passed ? (
                                <span className="text-xs font-medium text-green-700">Passed</span>
                              ) : (
                                <span className="text-xs text-gray-500">{record.attended ? "Not passed" : "Absent"}</span>
                              )}
                            </td>
                            <td className="max-w-[16rem] truncate text-gray-600" title={record.feedback || undefined}>
                              {record.feedback || <span className="text-gray-300">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pager
                    first={firstCompletionRow}
                    shown={pagedCompletions.length}
                    total={visibleCompletions.length}
                    noun="results"
                    page={completionPage}
                    pageCount={completionPageCount}
                    onPage={setCompletionPage}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {schedulingTraining && (
        <ScheduleTrainingModal
          isSaving={addTraining.isPending}
          error={addTraining.error}
          onSubmit={(values) => addTraining.mutate(values, { onSuccess: () => setSchedulingTraining(false) })}
          onClose={() => setSchedulingTraining(false)}
        />
      )}
      {viewingTraining && (
        <TrainingSessionModal
          key={viewingTraining.id}
          training={viewingTraining}
          records={trainingAttendance.data ?? []}
          profiles={profiles.data ?? []}
          canManage={canManage}
          isSaving={addTrainingAttendance.isPending}
          error={addTrainingAttendance.error}
          onRecord={(values, done) => addTrainingAttendance.mutate(values, { onSuccess: done })}
          onClose={() => {
            addTrainingAttendance.reset();
            setViewingTrainingId(null);
          }}
        />
      )}
      {tab === "setup" && canManage && (
        <div className="space-y-6">
          <div className="grid gap-4 lg:grid-cols-3">
            <form onSubmit={submitDepartment} className="card">
              <div className="card-content space-y-3">
                <h3 className="font-semibold">Department</h3>
                <input
                  required
                  name="name"
                  className="input"
                  placeholder="Name"
                />
                <input
                  required
                  name="code"
                  className="input"
                  placeholder="Code"
                />
                <select required name="department_type" className="input">
                  <option value="academic">Academic</option>
                  <option value="administrative">Administrative</option>
                  <option value="finance">Finance</option>
                  <option value="student_services">Student services</option>
                  <option value="operations">Operations</option>
                  <option value="other">Other</option>
                </select>
                <input
                  name="description"
                  className="input"
                  placeholder="Description"
                />
                <button className="btn btn-primary w-full">
                  Add department
                </button>
              </div>
            </form>
            <form onSubmit={submitPosition} className="card">
              <div className="card-content space-y-3">
                <h3 className="font-semibold">Position</h3>
                <input
                  required
                  name="title"
                  className="input"
                  placeholder="Title"
                />
                <select name="department" className="input">
                  <option value="">School-wide / no department</option>
                  {departments.data?.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
                <select name="level" className="input">
                  <option value="entry">Entry</option>
                  <option value="junior">Junior</option>
                  <option value="senior">Senior</option>
                  <option value="manager">Manager</option>
                </select>
                <input
                  min="0"
                  type="number"
                  name="experience"
                  defaultValue="0"
                  className="input"
                />
                <button className="btn btn-primary w-full">Add position</button>
              </div>
            </form>
            <form onSubmit={submitLeaveType} className="card">
              <div className="card-content space-y-3">
                <h3 className="font-semibold">Leave type</h3>
                <input
                  required
                  name="name"
                  className="input"
                  placeholder="Name"
                />
                <input
                  required
                  name="code"
                  className="input"
                  placeholder="Code"
                />
                <input
                  required
                  min="1"
                  type="number"
                  name="days"
                  className="input"
                  placeholder="Days per year"
                />
                <button className="btn btn-primary w-full">
                  Add leave type
                </button>
              </div>
            </form>
          </div>
          <section>
            <div className="mb-3">
              <h2 className="text-lg font-semibold">Department directory</h2>
              <p className="text-sm text-gray-500">
                Departments created for this school and the staff positions
                assigned to each one.
              </p>
            </div>
            {departments.isLoading ? (
              <div className="card p-6 text-sm text-gray-500">
                Loading departments…
              </div>
            ) : departments.data?.length ? (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {departments.data.map((department) => {
                  const departmentPositions =
                    positions.data?.filter(
                      (position) => position.department === department.id,
                    ) || [];
                  const departmentStaff =
                    profiles.data?.filter(
                      (profile) => profile.department === department.id,
                    ) || [];
                  return (
                    <article key={department.id} className="card">
                      <div className="card-content pt-6">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-primary-600">
                              {department.code}
                            </div>
                            <h3 className="text-lg font-semibold">
                              {department.name}
                            </h3>
                          </div>
                          <span
                            className={`rounded-full px-2 py-1 text-xs ${department.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}
                          >
                            {department.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <p className="mt-2 min-h-10 text-sm text-gray-600">
                          {department.description || "No description provided."}
                        </p>
                        <dl className="mt-4 grid grid-cols-3 gap-2 border-y py-3 text-center">
                          <div>
                            <dt className="text-xs text-gray-500">Staff</dt>
                            <dd className="font-semibold">
                              {departmentStaff.length}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500">Positions</dt>
                            <dd className="font-semibold">
                              {departmentPositions.length}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500">Head</dt>
                            <dd
                              className="truncate text-sm font-medium"
                              title={department.head_name || "Not assigned"}
                            >
                              {department.head_name || "Unassigned"}
                            </dd>
                          </div>
                        </dl>
                        <div className="mt-4">
                          <h4 className="text-xs font-semibold uppercase text-gray-500">
                            Positions
                          </h4>
                          {departmentPositions.length ? (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {departmentPositions.map((position) => (
                                <span
                                  key={position.id}
                                  className="rounded bg-gray-100 px-2 py-1 text-xs"
                                >
                                  {position.title} ·{" "}
                                  <span className="capitalize">
                                    {position.level}
                                  </span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-2 text-sm text-gray-500">
                              No positions added yet.
                            </p>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed bg-white p-8 text-center text-sm text-gray-500">
                No departments have been created yet. Use the Department form
                above.
              </div>
            )}
          </section>
          <div className="grid gap-6 lg:grid-cols-2">
            <section className="card">
              <div className="card-header">
                <h2 className="font-semibold">All positions</h2>
                <p className="text-sm text-gray-500">
                  Job titles available when creating staff profiles.
                </p>
              </div>
              <div className="card-content">
                <div className="divide-y">
                  {positions.data?.map((position) => (
                    <div
                      key={position.id}
                      className="flex items-center justify-between gap-3 py-3"
                    >
                      <div>
                        <div className="font-medium">{position.title}</div>
                        <div className="text-sm text-gray-500">
                          {position.department_name || "School-wide"} ·{" "}
                          <span className="capitalize">{position.level}</span> ·{" "}
                          {position.experience_required} years experience
                        </div>
                      </div>
                      <span
                        className={`rounded px-2 py-1 text-xs ${position.is_vacant ? "bg-primary-100 text-primary-700" : "bg-gray-100 text-gray-600"}`}
                      >
                        {position.is_vacant ? "Vacant" : "Filled"}
                      </span>
                    </div>
                  ))}
                  {!positions.data?.length && (
                    <p className="py-6 text-center text-sm text-gray-500">
                      No positions created.
                    </p>
                  )}
                </div>
              </div>
            </section>
            <section className="card">
              <div className="card-header">
                <h2 className="font-semibold">Leave types</h2>
                <p className="text-sm text-gray-500">
                  Leave policies available to staff.
                </p>
              </div>
              <div className="card-content">
                <div className="divide-y">
                  {leaveTypes.data?.map((type) => (
                    <div
                      key={type.id}
                      className="flex items-center justify-between gap-3 py-3"
                    >
                      <div>
                        <div className="font-medium">
                          {type.name}{" "}
                          <span className="text-xs text-gray-500">
                            ({type.code})
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {type.is_paid ? "Paid" : "Unpaid"} ·{" "}
                          {type.requires_approval
                            ? "Approval required"
                            : "No approval required"}
                        </div>
                      </div>
                      <span className="font-semibold">
                        {type.max_days_per_year} days
                      </span>
                    </div>
                  ))}
                  {!leaveTypes.data?.length && (
                    <p className="py-6 text-center text-sm text-gray-500">
                      No leave types created.
                    </p>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
